import { AppBarStyles } from '@client/style/style.constants';
import {
  useDeselectElement,
  useIsProjectList,
  useIsSharedProjectRoute,
} from '@client/hooks/use-router.hooks';
import { useIsReadonly, useUser } from '@client/hooks/use-user.hooks';
import { useOrganizationGFAQuotaIsExceeded } from '@client/store/organization';
import { useSelectedOrganizationName } from '@client/store/organization';
import {
  AppBar as MuiAppBar,
  Box,
  ButtonBase,
  SxProps,
  Toolbar,
  Tooltip,
} from '@mui/material';
import { mergeSx } from '@client/helpers/mui.helpers';
import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { NODON_PROJECTS_LIST_PATH } from '@shared/constants/router.constants';
import GFAQuotaExceededAlert from '@client/pages/MainView/components/AppBar/components/GFAQuotaExceededAlert';
import OrganizationsMenu from '@client/pages/components/OrganizationsMenu';
import ProfileMenu from '@features/users/ProfileMenu';
import { ShareProjectButton } from '@features/projects/ShareProjectButton';
import ProjectSettings from './components/ProjectSettings';
import { ProjectSelectorToggle } from './components/ProjectSelectorToggle';
import { ProjectSelectorButton } from './components/ProjectSelectorButton';
import PageNavigation from './components/PageNavigation';
import SearchProjects from '@client/pages/components/filtering/SearchProjects';
import { CreateProjectMenu } from '@features/projects/CreateProjectMenu';
import { useAppbarStyles } from './hooks/use-appbar-styles';
import UndoRedoButtons from './components/UndoRedoButtons';
import { AppWorkIndicator } from './components/AppWorkIndicator';
import { expressionPanel } from '@features/expressions/ExpressionInputPanel/ExpressionInputPanel';
import { closeProjectWithAnimation } from '@features/projects/lists/helpers/project-open-animation.helpers';
import { useProjectState, useProjectsForOverview } from '@client/store/project';
import { useProjectOpening } from '@features/projects/project-opening.store';
import { ProjectSortControl } from '@features/projects/ProjectSortControl';
import { shouldShowProjectListChrome } from '@features/projects/helpers/project-list-chrome.helpers';
import { useProjectViewSwitchPosition } from './hooks/use-project-view-switch-position';

const projectListChromeFadeSx = {
  transition: 'opacity 200ms ease',
} as const;

export const AppBar: React.FC = () => {
  const { classes } = useAppbarStyles();
  const isProjectList = useIsProjectList();
  const projects = useProjectsForOverview();
  const showProjectListChrome =
    !isProjectList || shouldShowProjectListChrome(projects.length);
  const user = useUser();
  const readonly = useIsReadonly();
  const isSharedProject = useIsSharedProjectRoute();
  const organizationGFAQuotaIsExceeded = useOrganizationGFAQuotaIsExceeded();
  const [isHovering, setIsHovering] = React.useState(false);
  const navigate = useNavigate();
  const organization = useSelectedOrganizationName();
  const { project } = useProjectState('project');
  const expressionPanelOpen = expressionPanel.useIsOpen();
  const rightControlsRef = useRef<HTMLDivElement>(null);
  const newProjectRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const viewSwitchRef = useRef<HTMLDivElement>(null);
  useProjectViewSwitchPosition(
    isProjectList && !!user,
    profileRef,
    viewSwitchRef,
  );

  useLayoutEffect(() => {
    const controls = rightControlsRef.current;
    const newProject = newProjectRef.current;
    const toolbar = controls?.parentElement;
    const header = controls?.closest<HTMLElement>('[data-app-header]');
    if (!isProjectList || !controls || !toolbar || !header || !newProject)
      return;
    const previousMinHeight = header.style.minHeight;
    const updateSearchWidth = () => {
      const bounds = header.getBoundingClientRect();
      const available =
        2 *
        (controls.getBoundingClientRect().left -
          bounds.left -
          bounds.width / 2 -
          48);
      toolbar.style.setProperty(
        '--project-list-search-max-width',
        available < 240 ? '60vw' : `${available}px`,
      );
      toolbar.style.setProperty(
        '--project-list-search-min-top',
        available < 240 ? '48px' : '0px',
      );
      header.style.minHeight = available < 240 ? '96px' : previousMinHeight;
    };
    updateSearchWidth();
    const observer = new ResizeObserver(updateSearchWidth);
    observer.observe(header);
    observer.observe(controls);
    observer.observe(newProject);
    return () => {
      observer.disconnect();
      header.style.minHeight = previousMinHeight;
      toolbar.style.removeProperty('--project-list-search-max-width');
      toolbar.style.removeProperty('--project-list-search-min-top');
    };
  }, [isProjectList, user]);

  const handleSelectorClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      if (isProjectList || !organization) return;
      void closeProjectWithAnimation(project.id, () => {
        navigate(generatePath(NODON_PROJECTS_LIST_PATH, { organization }), {
          flushSync: true,
        });
      });
    },
    [isProjectList, navigate, organization, project.id],
  );

  return (
    <AppBarContainer>
      <Toolbar disableGutters className={classes.toolbar}>
        <Box
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          sx={mergeSx(
            appBarToolbarSx,
            isProjectList ? { alignItems: 'flex-start' } : undefined,
          )}
        >
          <Box
            sx={mergeSx(
              appBarLeftSx,
              isProjectList ? { height: AppBarStyles.HEIGHT } : undefined,
            )}
          >
            {isProjectList ? (
              <ButtonBase
                aria-label="Scroll to top"
                disableRipple
                sx={{
                  transformOrigin: 'left center',
                  transform:
                    'translate(clamp(0px, calc((var(--project-list-app-header-height, 240px) - 48px) / 12), 16px), clamp(0px, calc((var(--project-list-app-header-height, 240px) - 48px) / 12), 16px)) scale(clamp(1, calc(1 + (var(--project-list-app-header-height, 240px) - 48px) / 384px), 1.5))',
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  document
                    .querySelector('[data-project-list-page]')
                    ?.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                <ProjectSelectorToggle />
              </ButtonBase>
            ) : (
              <>
                <Tooltip title="Projects" placement="bottom">
                  <Box
                    className={classes.projectSelectorContainer}
                    onClick={isSharedProject ? undefined : handleSelectorClick}
                    sx={mergeSx(
                      projectSelectorRowSx,
                      !isProjectList && !isSharedProject
                        ? selectorContainerSx
                        : undefined,
                    )}
                  >
                    <ProjectSelectorToggle />
                    <ProjectSelectorButton hideGfa={isHovering} />
                  </Box>
                </Tooltip>
                {!isSharedProject && <ProjectSettings hidden={!isHovering} />}
              </>
            )}
          </Box>

          <Box
            data-project-list-header-search={isProjectList ? '' : undefined}
            sx={mergeSx(
              appBarCenterSx,
              isProjectList
                ? {
                    ...projectListSearchPositionSx,
                    ...projectListChromeFadeSx,
                    opacity: showProjectListChrome ? 1 : 0,
                    pointerEvents: showProjectListChrome ? 'auto' : 'none',
                    visibility: showProjectListChrome ? 'visible' : 'hidden',
                  }
                : undefined,
            )}
          >
            {isProjectList ? (
              <>
                <SearchProjects />
                <Box
                  data-project-list-header-sort=""
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    marginLeft: '16px',
                    ...projectListChromeFadeSx,
                    opacity: showProjectListChrome
                      ? 'clamp(0, calc((var(--project-list-app-header-height, 240px) - 72px) / 48px), 1)'
                      : 0,
                    visibility: showProjectListChrome
                      ? 'var(--project-list-sort-visibility, visible)'
                      : 'hidden',
                  }}
                >
                  <ProjectSortControl />
                </Box>
              </>
            ) : (
              <PageNavigation />
            )}
          </Box>

          <Box
            ref={rightControlsRef}
            sx={mergeSx(
              appBarRightSx,
              isProjectList
                ? {
                    width: 'auto',
                    pointerEvents: 'none',
                    '& > *': { pointerEvents: 'auto' },
                    height: AppBarStyles.HEIGHT,
                    transform:
                      'translate(clamp(-12px, calc((48px - var(--project-list-app-header-height, 240px)) / 16), 0px), clamp(0px, calc((var(--project-list-app-header-height, 240px) - 48px) / 12), 16px))',
                    gap: 'clamp(12px, calc(12px + (var(--project-list-app-header-height, 240px) - 48px) / 16), 24px)',
                  }
                : undefined,
            )}
          >
            <AppWorkIndicator />
            {!isProjectList && !readonly && !expressionPanelOpen && (
              <UndoRedoButtons />
            )}
            {isProjectList ? (
              <>
                {user && (
                  <OrganizationsMenu organizations={user.organizations} />
                )}
                <Box
                  ref={newProjectRef}
                  sx={{ display: 'flex', flexShrink: 0 }}
                >
                  <CreateProjectMenu headerStyle buttonSize="large" />
                </Box>
              </>
            ) : (
              !readonly && <ShareProjectButton />
            )}
            {!isProjectList && user && (
              <OrganizationsMenu organizations={user.organizations} />
            )}
            {user && (
              <Box ref={profileRef} sx={{ display: 'flex', flexShrink: 0 }}>
                <ProfileMenu user={user} />
              </Box>
            )}
          </Box>
        </Box>
        {isProjectList && (
          <Box
            ref={viewSwitchRef}
            id="project-list-view-tabs"
            data-project-list-view-tabs=""
            sx={{
              position: 'absolute',
              right: 'var(--project-list-profile-center-right, 24px)',
              top: 'calc(max(var(--project-list-search-min-top, 0px), (var(--project-list-app-header-height, 240px) - 48px) * 0.666666667) + 24px)',
              transform: 'translate(50%, -50%)',
              display: showProjectListChrome ? 'flex' : 'none',
              visibility: 'hidden',
              ...projectListChromeFadeSx,
              opacity: showProjectListChrome ? 1 : 0,
            }}
          />
        )}
        {organizationGFAQuotaIsExceeded && <GFAQuotaExceededAlert />}
      </Toolbar>
    </AppBarContainer>
  );
};

const appBarToolbarSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  px: '12px',
} as const;

const appBarLeftSx = {
  width: 1 / 3,
  gap: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  minWidth: 0,
} as const;

const appBarCenterSx = {
  width: 1 / 3,
  minWidth: 0,
  display: 'flex',
  justifyContent: 'center',
} as const;

// Keep the short header's search position, then reach a 2:1 top/bottom gap at 240 px.
const projectListSearchPositionSx = {
  width: '60%',
  minWidth: 'min(240px, calc(100% - 208px))',
  maxWidth:
    'min(calc(100% - 208px), calc(var(--project-list-search-max-width, 60vw) + max(0px, calc((var(--project-list-app-header-height, 240px) - 120px) * 10))))',
  height: AppBarStyles.HEIGHT,
  alignItems: 'center',
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  top: `max(var(--project-list-search-min-top, 0px), calc((var(--project-list-app-header-height, ${AppBarStyles.HEIGHT * 5}px) - ${AppBarStyles.HEIGHT}px) * 0.666666667))`,
} as const;

const appBarRightSx = {
  width: 1 / 3,
  gap: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
} as const;

const projectSelectorRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
} as const;

const selectorContainerSx: SxProps = {
  cursor: 'pointer',
};

const AppBarContainer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { classes } = useAppbarStyles();
  const deselectElement = useDeselectElement();
  const isProjectList = useIsProjectList();
  const opening = useProjectOpening((state) => state.phase !== 'idle');
  const hidden = isProjectList && opening;

  return (
    <MuiAppBar
      data-app-header=""
      inert={hidden}
      sx={{
        opacity: hidden ? 0 : 1,
        pointerEvents: hidden ? 'none' : undefined,
        transition: 'opacity 150ms ease-out',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
      position="fixed"
      color="inherit"
      elevation={0}
      className={classes.appBar}
      onClick={deselectElement}
    >
      {children}
    </MuiAppBar>
  );
};
